import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FaUserTie, FaEdit, FaTrash, FaPlus, FaSearch, FaEnvelope, FaBuilding, FaCalendarAlt, FaPhone, FaTimes, FaToggleOn, FaToggleOff, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import './AdminManagers.css';

const AdminManagers = () => {
    const navigate = useNavigate();
    const baseUrl = "https://api.burrayhr.com";
    const [adminManagers, setAdminManagers] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        fetchAdminManagers();
        fetchCompanies();
    }, []);

    const fetchAdminManagers = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${baseUrl}/api/admin-managers/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            console.log(response.data);
            setAdminManagers(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching admin managers:', error);
            setError(error.response?.data?.message || 'Failed to fetch admin managers');
            setLoading(false);
        }
    };

    const fetchCompanies = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${baseUrl}/api/companies/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setCompanies(response.data);
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    };

    const handleDelete = async (id, e) => {
        if (e) e.stopPropagation();
        
        try {
            const result = await Swal.fire({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#ef4444',
                cancelButtonColor: '#64748b',
                confirmButtonText: 'Yes, delete it!'
            });

            if (result.isConfirmed) {
                const token = localStorage.getItem('token');
                await axios.delete(`${baseUrl}/api/admin-managers/${id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'Admin manager has been deleted.',
                    confirmButtonColor: '#1e3a8a'
                });
                
                // Update the state to remove the deleted admin manager
                setAdminManagers(adminManagers.filter(admin => admin.id !== id));
                
                // Close modal if open
                if (showModal && selectedAdmin && selectedAdmin.id === id) {
                    closeModal();
                }
            }
        } catch (error) {
            console.log(error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: 'Failed to delete admin manager.',
                confirmButtonColor: '#1e3a8a'
            });
        }
    };

    // New function to handle account activation/deactivation
    const handleToggleStatus = async (admin, e) => {
        if (e) e.stopPropagation();
        
        const newStatus = admin.is_active ? false : true;
        const actionText = newStatus ? 'activate' : 'deactivate';
        
        try {
            const result = await Swal.fire({
                title: `${newStatus ? 'Activate' : 'Deactivate'} Account?`,
                text: `Are you sure you want to ${actionText} ${admin.first_name || ''} ${admin.last_name || ''}'s account?`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: newStatus ? '#10b981' : '#ef4444',
                cancelButtonColor: '#64748b',
                confirmButtonText: `Yes, ${actionText} it!`
            });

            if (result.isConfirmed) {
                const token = localStorage.getItem('token');
                const response = await axios.patch(`${baseUrl}/api/admin-managers/${admin.id}/`, 
                    { is_active: newStatus },
                    {
                        headers: { 'Authorization': `Bearer ${token}` }
                    }
                );

                // Update the admin manager in the state
                const updatedAdmin = { ...admin, is_active: newStatus };
                setAdminManagers(adminManagers.map(a => 
                    a.id === admin.id ? updatedAdmin : a
                ));
                
                // Update selected admin if it's the one being toggled
                if (selectedAdmin && selectedAdmin.id === admin.id) {
                    setSelectedAdmin(updatedAdmin);
                }

                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: `Account ${newStatus ? 'activated' : 'deactivated'} successfully.`,
                    confirmButtonColor: '#1e3a8a'
                });
            }
        } catch (error) {
            console.log(error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: `Failed to ${actionText} account.`,
                confirmButtonColor: '#1e3a8a'
            });
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    // Update the filtering function with null checks
    const filteredAdminManagers = adminManagers.filter(admin => {
        if (!admin) return false;
        
        const firstName = admin.first_name || '';
        const lastName = admin.last_name || '';
        const email = admin.email || '';
        const searchTermLower = searchTerm.toLowerCase();
        
        return firstName.toLowerCase().includes(searchTermLower) ||
               lastName.toLowerCase().includes(searchTermLower) ||
               email.toLowerCase().includes(searchTermLower);
    });

    const openAdminDetails = (admin) => {
        setSelectedAdmin(admin);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedAdmin(null);
    };

    const handleModalBackdropClick = (e) => {
        if (e.target.classList.contains('admin-modal-backdrop')) {
            closeModal();
        }
    };

    // Helper function to safely display admin name
    const getAdminName = (admin) => {
        if (!admin) return 'NA';
        
        const firstName = admin.first_name || '';
        const lastName = admin.last_name || '';
        
        if (!firstName && !lastName) return 'NA';
        return `${firstName} ${lastName}`.trim();
    };

    return (
        <div>
            <Topbar />
            <Sidebar />
            <div className="main-content">
                <div className="admin-managers-container">
                    <div className="admin-managers-header">
                        <div className="title-section">
                            <FaUserTie className="header-icon" />
                            <h1>Admin Managers</h1>
                        </div>
                        <div className="actions-section">
                            <div className="search-box">
                                <FaSearch className="search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search admin managers..."
                                    value={searchTerm}
                                    onChange={handleSearch}
                                />
                            </div>
                            <button 
                                className="add-button"
                                onClick={() => navigate('/admin-managers/add')}
                            >
                                <FaPlus /> Add Admin Manager
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="loading-spinner">
                            <div className="spinner"></div>
                            <p>Loading admin managers...</p>
                        </div>
                    ) : error ? (
                        <div className="error-message">
                            <p>{error}</p>
                        </div>
                    ) : (
                        <div className="table-container">
                            <table className="admin-managers-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Company</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAdminManagers.map(admin => (
                                        <tr key={admin.id} onClick={() => openAdminDetails(admin)} className="admin-row">
                                            <td>{getAdminName(admin)}</td>
                                            <td>{admin.email || 'No email'}</td>
                                            <td>{admin.company || 'Not assigned'}</td>
                                            <td>
                                                <span className={`status-badge ${admin.is_active ? 'active' : 'inactive'}`}>
                                                    {admin.is_active ? 
                                                        <><FaCheckCircle /> Active</> : 
                                                        <><FaTimesCircle /> Inactive</>
                                                    }
                                                </span>
                                            </td>
                                            <td className="action-buttons" onClick={(e) => e.stopPropagation()}>
                                                <button 
                                                    className={`status-btn ${admin.is_active ? 'deactivate' : 'activate'}`}
                                                    onClick={(e) => handleToggleStatus(admin, e)}
                                                    title={admin.is_active ? 'Deactivate Account' : 'Activate Account'}
                                                >
                                                    {admin.is_active ? 
                                                        <><FaToggleOff /> Deactivate</> : 
                                                        <><FaToggleOn /> Activate</>
                                                    }
                                                </button>
                                                <button 
                                                    className="delete-btn"
                                                    onClick={(e) => handleDelete(admin.id, e)}
                                                >
                                                    <FaTrash /> Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {filteredAdminManagers.length === 0 && (
                                <div className="no-results">
                                    <FaUserTie className="no-results-icon" />
                                    <p>No admin managers found</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Admin Manager Details Modal */}
            {showModal && selectedAdmin && (
                <div className="admin-modal-backdrop" onClick={handleModalBackdropClick}>
                    <div className="admin-modal">
                        <div className="admin-modal-header">
                            <h2>{getAdminName(selectedAdmin) !== 'NA' ? 
                                getAdminName(selectedAdmin) : 'Admin Manager'}</h2>
                            <div className="admin-status">
                                <span className={`status-badge ${selectedAdmin.is_active ? 'active' : 'inactive'}`}>
                                    {selectedAdmin.is_active ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                            <button className="modal-close-btn" onClick={closeModal}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="admin-modal-content">
                            <div className="admin-detail-item">
                                <FaUserTie className="detail-icon" />
                                <div>
                                    <h4>First Name</h4>
                                    <p>{selectedAdmin.first_name || 'Not specified'}</p>
                                </div>
                            </div>
                            <div className="admin-detail-item">
                                <FaUserTie className="detail-icon" />
                                <div>
                                    <h4>Last Name</h4>
                                    <p>{selectedAdmin.last_name || 'Not specified'}</p>
                                </div>
                            </div>
                            <div className="admin-detail-item">
                                <FaEnvelope className="detail-icon" />
                                <div>
                                    <h4>Email</h4>
                                    <p>{selectedAdmin.email || 'No email provided'}</p>
                                </div>
                            </div>
                            <div className="admin-detail-item">
                                <FaPhone className="detail-icon" />
                                <div>
                                    <h4>Phone</h4>
                                    <p>{selectedAdmin.employee_details.phone_number || 'No phone provided'}</p>
                                </div>
                            </div>
                            <div className="admin-detail-item">
                                <FaBuilding className="detail-icon" />
                                <div>
                                    <h4>Company</h4>
                                    <p>{selectedAdmin.company || 'Not assigned'}</p>
                                </div>
                            </div>
                            <div className="admin-detail-item">
                                <FaCalendarAlt className="detail-icon" />
                                <div>
                                    <h4>Created At</h4>
                                    <p>{selectedAdmin.created_at ? 
                                        new Date(selectedAdmin.created_at).toLocaleDateString() : 
                                        'Not available'}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminManagers;
